import React, { useEffect, useState } from 'react';
import { db, storage } from '../../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Pencil, Trash2, PlusCircle, UploadCloud } from 'lucide-react';
import { fetchEquipesChampionnat } from '../../api/fff';

const defaultForm = {
  nom: '',
  slug: '',
  photo: '',
  slogan: '',
  description: '',
  ordre: 1,
  couleur: '#001B56',
  visible: true,
};

const Teams = () => {
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [photoFile, setPhotoFile] = useState(null);
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [editId, setEditId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'equipes'), orderBy('ordre', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setEquipes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handlePhotoChange = e => {
    if (e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
    }
  };

  const uploadPhoto = async (file, nom, equipeId = null) => {
    if (!file) return '';
    const ext = file.name.split('.').pop();
    const safeName = nom.replace(/[^a-zA-Z0-9]/g, '_');
    const path = `photos_equipes/${safeName}_${Date.now()}${equipeId ? '_' + equipeId : ''}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    return await getDownloadURL(storageRef);
  };

  const handleAddOrEditEquipe = async (e) => {
    e.preventDefault();
    setAdding(true);
    setFeedback('');
    try {
      let photoUrl = form.photo;
      if (photoFile) {
        photoUrl = await uploadPhoto(photoFile, form.nom, editId);
      }
      const equipeData = {
        ...form,
        photo: photoUrl,
        ordre: Number(form.ordre) || 1,
        visible: !!form.visible,
        updatedAt: new Date(),
      };
      if (editId) {
        await updateDoc(doc(db, 'equipes', editId), equipeData);
        setFeedback('Équipe modifiée avec succès !');
      } else {
        await addDoc(collection(db, 'equipes'), {
          ...equipeData,
          createdAt: new Date(),
        });
        setFeedback('Équipe ajoutée avec succès !');
      }
      setShowModal(false);
      setForm(defaultForm);
      setPhotoFile(null);
      setEditId(null);
    } catch (err) {
      setFeedback("Erreur : " + err.message);
    }
    setAdding(false);
    setTimeout(() => setFeedback(''), 3000);
  };

  const handleEdit = (equipe) => {
    setForm({
      nom: equipe.nom || '',
      slug: equipe.slug || '',
      photo: equipe.photo || '',
      slogan: equipe.slogan || '',
      description: equipe.description || '',
      ordre: equipe.ordre ?? 1,
      couleur: equipe.couleur || '#001B56',
      visible: equipe.visible !== false,
    });
    setEditId(equipe.id);
    setShowModal(true);
    setPhotoFile(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      if (toDelete.photo && toDelete.photo.includes('photos_equipes')) {
        try {
          const photoRef = ref(storage, toDelete.photo);
          await deleteObject(photoRef);
        } catch (e) { /* ignore */ }
      }
      await deleteDoc(doc(db, 'equipes', toDelete.id));
      setFeedback('Équipe supprimée.');
    } catch (err) {
      setFeedback("Erreur lors de la suppression : " + err.message);
    }
    setShowConfirm(false);
    setToDelete(null);
    setTimeout(() => setFeedback(''), 3000);
  };

  const handleImportEquipesFFF = async () => {
    setFeedback('');
    try {
      const fffEquipes = await fetchEquipesChampionnat();
      // Récupérer les noms déjà présents pour éviter les doublons
      const nomsExistants = new Set(equipes.map(eq => eq.nom));
      let ajout = 0;
      for (const eq of fffEquipes) {
        if (!nomsExistants.has(eq.championnat)) {
          // Extraire l'id numérique de l'API (ex: '/api/equipes/1' => '1')
          let fffId = '';
          if (eq.id) {
            const parts = eq.id.split('/');
            fffId = parts[parts.length - 1];
          }
          await addDoc(collection(db, 'equipes'), {
            nom: eq.championnat,
            slug: eq.championnat.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            photo: '',
            slogan: '',
            description: '',
            ordre: 99,
            couleur: '#001B56',
            visible: false,
            createdAt: new Date(),
            updatedAt: new Date(),
            fffId: fffId, // Ajout de l'id FFF numérique
          });
          ajout++;
        }
      }
      setFeedback(`${ajout} équipe(s) importée(s) depuis la FFF !`);
    } catch (err) {
      setFeedback('Erreur import FFF : ' + err.message);
    }
    setTimeout(() => setFeedback(''), 3000);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Gestion des Équipes</h1>
      <div className="flex items-center gap-4 mb-6">
        <button className="flex items-center gap-2 bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold px-4 py-2 rounded-xl shadow transition" onClick={() => { setShowModal(true); setEditId(null); setForm(defaultForm); setPhotoFile(null); }}>
          <PlusCircle size={20} /> Ajouter une équipe
        </button>
        <button className="flex items-center gap-2 bg-[#BDFF00] hover:bg-[#FFE400] text-[#001B56] font-bold px-4 py-2 rounded-xl shadow transition" onClick={handleImportEquipesFFF}>
          <UploadCloud size={20} /> Importer les équipes FFF
        </button>
      </div>
      {feedback && <div className="mb-4 text-white font-semibold">{feedback}</div>}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <form onSubmit={handleAddOrEditEquipe} className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl flex flex-col gap-4 animate-fade-in">
            <h2 className="text-xl font-bold mb-2">{editId ? 'Modifier l\'équipe' : 'Ajouter une équipe'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1">Nom</label>
                <input type="text" name="nom" value={form.nom} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Slug (url)</label>
                <input type="text" name="slug" value={form.slug} onChange={handleChange} required className="w-full px-3 py-2 rounded bg-[#001B56] text-white" placeholder="ex: pro, feminines, n3, u19..." />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-1">Photo</label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={handlePhotoChange} className="w-full text-white" />
                  {form.photo && !photoFile && (
                    <img src={form.photo} alt="photo" className="w-10 h-10 rounded-full bg-white" />
                  )}
                  {photoFile && (
                    <UploadCloud size={22} className="text-[#FFE400] animate-bounce" />
                  )}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block mb-1">Slogan</label>
                <input type="text" name="slogan" value={form.slogan} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div className="md:col-span-2">
                <label className="block mb-1">Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white min-h-[60px]" />
              </div>
              <div>
                <label className="block mb-1">Ordre (slider)</label>
                <input type="number" name="ordre" value={form.ordre} onChange={handleChange} className="w-full px-3 py-2 rounded bg-[#001B56] text-white" />
              </div>
              <div>
                <label className="block mb-1">Couleur dominante</label>
                <input type="color" name="couleur" value={form.couleur} onChange={handleChange} className="w-12 h-10 p-0 border-none rounded" />
              </div>
              <div className="flex items-center gap-3 mt-2 md:col-span-2">
                <input type="checkbox" name="visible" checked={form.visible} onChange={handleChange} id="visible" />
                <label htmlFor="visible">Afficher cette équipe sur le site public</label>
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-4">
              <button type="button" className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowModal(false); setEditId(null); setPhotoFile(null); }} disabled={adding}>Annuler</button>
              <button type="submit" className="px-6 py-2 rounded bg-[#FFE400] hover:bg-[#BDFF00] text-[#001B56] font-bold" disabled={adding}>{adding ? (editId ? 'Modification...' : 'Ajout...') : (editId ? 'Modifier' : 'Ajouter')}</button>
            </div>
          </form>
        </div>
      )}
      {/* Confirmation suppression */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-[#00238D] text-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 animate-fade-in">
            <h2 className="text-xl font-bold">Confirmer la suppression</h2>
            <p>Voulez-vous vraiment supprimer cette équipe ? Cette action est irréversible.</p>
            <div className="flex justify-end gap-4">
              <button className="px-4 py-2 rounded bg-gray-400 hover:bg-gray-500 text-white font-bold" onClick={() => { setShowConfirm(false); setToDelete(null); }}>Annuler</button>
              <button className="px-6 py-2 rounded bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleDelete}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {loading ? (
        <div className="text-white">Chargement...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-[#00238D] rounded-xl shadow-lg">
            <thead>
              <tr className="text-[#FFE400] text-left">
                <th className="py-2 px-4">Photo</th>
                <th className="py-2 px-4">Nom</th>
                <th className="py-2 px-4">Slug</th>
                <th className="py-2 px-4">Slogan</th>
                <th className="py-2 px-4">Ordre</th>
                <th className="py-2 px-4">Visible</th>
                <th className="py-2 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {equipes.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-white py-8">Aucune équipe enregistrée.</td></tr>
              ) : (
                equipes.map(equipe => (
                  <tr key={equipe.id} className="border-b border-[#001B56] hover:bg-[#001B56]/40 transition">
                    <td className="py-2 px-4">
                      {equipe.photo ? (
                        <img src={equipe.photo} alt={equipe.nom} className="w-10 h-10 rounded-full object-cover bg-white" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#FFE400] flex items-center justify-center text-[#001B56] font-bold">
                          {equipe.nom?.[0] || '?'}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-4 font-bold">{equipe.nom}</td>
                    <td className="py-2 px-4">{equipe.slug}</td>
                    <td className="py-2 px-4">{equipe.slogan}</td>
                    <td className="py-2 px-4">{equipe.ordre}</td>
                    <td className="py-2 px-4">{equipe.visible ? 'Oui' : 'Non'}</td>
                    <td className="py-2 px-4 flex gap-2">
                      <button className="p-2 rounded hover:bg-[#FFE400]/20 transition" title="Éditer" onClick={() => handleEdit(equipe)}><Pencil size={18} /></button>
                      <button className="p-2 rounded hover:bg-red-500/30 transition" title="Supprimer" onClick={() => { setShowConfirm(true); setToDelete(equipe); }}><Trash2 size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Teams; 